import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMonth, setUserMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [userDetail, setUserDetail] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [deptFilterSearch, setDeptFilterSearch] = useState('');
  const [deptFilterOpen, setDeptFilterOpen] = useState(false);
  const deptFilterRef = useRef(null);
  const [todayStatusFilter, setTodayStatusFilter] = useState(null);
  const dropdownRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'employee', department_id: null, company_id: null });
  const [addingUser, setAddingUser] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deptSearch, setDeptSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', company_id: null });
  const [savingEdit, setSavingEdit] = useState(false);
  const [managerDepts, setManagerDepts] = useState([]);
  const [editMgrDepts, setEditMgrDepts] = useState([]);
  const [userMgrDepts, setUserMgrDepts] = useState({}); // { userId: [{id, name}, ...] }
  const [companies, setCompanies] = useState([]);

  const fetchUsers = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch managed departments for all manager/admin users
  useEffect(() => {
    if (!isAdmin || users.length === 0) return;
    const managers = users.filter(u => u.role === 'manager' || u.role === 'admin');
    if (managers.length === 0) return;
    Promise.all(
      managers.map(m =>
        api.get(`/users/manager-departments/${m.id}`)
          .then(res => ({ userId: m.id, depts: res.data.data || [] }))
          .catch(() => ({ userId: m.id, depts: [] }))
      )
    ).then(results => {
      const map = {};
      for (const r of results) map[r.userId] = r.depts;
      setUserMgrDepts(map);
    });
  }, [users, isAdmin]);

  useEffect(() => {
    const userId = searchParams.get('user');
    const status = searchParams.get('status');
    if (userId) {
      setSelectedUser(Number(userId));
    }
    if (status) {
      setTodayStatusFilter(status);
    }
    if (userId || status) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Close dept filter on outside click
  useEffect(() => {
    if (!deptFilterOpen) return;
    const handleClick = (e) => {
      if (deptFilterRef.current && !deptFilterRef.current.contains(e.target)) {
        setDeptFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [deptFilterOpen]);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!editingId && !editingStatusId && !editingDeptId && !editingCompanyId) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEditingId(null);
        setEditingStatusId(null);
        setEditingDeptId(null);
        setEditingCompanyId(null);
        setDropdownPos(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [editingId, editingStatusId, editingDeptId, editingCompanyId]);

  const fetchUserDetail = useCallback(async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`, { params: { month: userMonth } });
      setUserDetail(response.data.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  }, [userMonth]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserDetail(selectedUser);
    }
  }, [selectedUser, fetchUserDetail]);

  const openRoleDropdown = (e, userId) => {
    e.stopPropagation();
    if (editingId === userId) {
      setEditingId(null);
      setDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 120;
    const spaceBelow = window.innerHeight - rect.bottom - 10;
    const top = spaceBelow >= dropdownHeight ? rect.bottom + 4 : rect.top - dropdownHeight - 4;
    setDropdownPos({ top, left: rect.left, maxHeight: dropdownHeight });
    setEditingId(userId);
    setEditingStatusId(null);
    setEditingDeptId(null);
    setEditingCompanyId(null);
  };

  const openStatusDropdown = (e, userId) => {
    e.stopPropagation();
    if (editingStatusId === userId) {
      setEditingStatusId(null);
      setDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 80;
    const spaceBelow = window.innerHeight - rect.bottom - 10;
    const top = spaceBelow >= dropdownHeight ? rect.bottom + 4 : rect.top - dropdownHeight - 4;
    setDropdownPos({ top, left: rect.left, maxHeight: dropdownHeight });
    setEditingStatusId(userId);
    setEditingId(null);
    setEditingDeptId(null);
    setEditingCompanyId(null);
  };

  const openDeptDropdown = async (e, userId) => {
    e.stopPropagation();
    if (editingDeptId === userId) {
      setEditingDeptId(null);
      setDropdownPos(null);
      setDeptSearch('');
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 300;
    const spaceBelow = window.innerHeight - rect.bottom - 10;
    const spaceAbove = rect.top - 10;

    let top, maxHeight;
    if (spaceBelow >= dropdownHeight) {
      top = rect.bottom + 4;
      maxHeight = dropdownHeight;
    } else if (spaceAbove >= dropdownHeight) {
      top = rect.top - dropdownHeight - 4;
      maxHeight = dropdownHeight;
    } else if (spaceAbove > spaceBelow) {
      maxHeight = spaceAbove;
      top = rect.top - maxHeight - 4;
    } else {
      top = rect.bottom + 4;
      maxHeight = spaceBelow;
    }

    setDropdownPos({ top, left: rect.left, maxHeight });
    setEditingDeptId(userId);
    setEditingId(null);
    setEditingStatusId(null);
    setEditingCompanyId(null);
    setDeptSearch('');

    // Load managed departments for manager users
    const targetUser = users.find(u => u.id === userId);
    if (targetUser && (targetUser.role === 'manager' || targetUser.role === 'admin')) {
      try {
        const res = await api.get(`/users/manager-departments/${userId}`);
        setEditMgrDepts((res.data.data || []).map(d => d.id));
      } catch { setEditMgrDepts([]); }
    }
  };

  const openCompanyDropdown = (e, userId) => {
    e.stopPropagation();
    if (editingCompanyId === userId) {
      setEditingCompanyId(null);
      setDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 200;
    const spaceBelow = window.innerHeight - rect.bottom - 10;
    const top = spaceBelow >= dropdownHeight ? rect.bottom + 4 : rect.top - dropdownHeight - 4;
    setDropdownPos({ top, left: rect.left, maxHeight: dropdownHeight });
    setEditingCompanyId(userId);
    setEditingId(null);
    setEditingStatusId(null);
    setEditingDeptId(null);
  };

  const updateUserCompany = async (userId, companyId) => {
    setUsers(prev => prev.map(u => u.id === userId ? {
      ...u,
      company_id: companyId,
      company: companyId ? companies.find(c => c.id === companyId) : null
    } : u));
    setEditingCompanyId(null);
    setDropdownPos(null);
    try {
      await api.put(`/users/${userId}`, { company_id: companyId });
    } catch (err) {
      toast.error('Failed to update company');
      fetchUsers(false);
    }
  };

  const updateUserRole = async (userId, role) => {
    const targetUser = users.find(u => u.id === userId);

    // Prevent removing the last admin
    if (targetUser?.role === 'admin' && role !== 'admin') {
      const adminCount = users.filter(u => u.role === 'admin').length;
      if (adminCount <= 1) {
        toast.error('Cannot remove the last admin');
        setEditingId(null);
        setDropdownPos(null);
        return;
      }
    }

    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    setEditingId(null);
    setDropdownPos(null);
    try {
      await api.put(`/users/${userId}`, { role });
    } catch (error) {
      toast.error('Failed to update role');
      fetchUsers(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, status } : u));
    setEditingStatusId(null);
    setDropdownPos(null);
    try {
      await api.put(`/users/${userId}`, { status });
    } catch (error) {
      toast.error('Failed to update status');
      fetchUsers(false);
    }
  };

  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);

  const fetchDepartments = useCallback(async () => {
    setDepartmentsLoading(true);
    try {
      const response = await api.get('/departments');
      console.log('DEPARTMENTS RAW:', JSON.stringify(response.data.data));
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error('Failed to load departments');
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  // Fetch companies (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/companies')
      .then(res => setCompanies(res.data.data || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Fetch manager's managed departments
  useEffect(() => {
    if (!isManager) return;
    api.get('/users/my-departments')
      .then(res => setManagerDepts(res.data.data || []))
      .catch(() => {});
  }, [isManager]);

  const updateUserDept = async (userId, dept) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, department: dept, department_id: dept?.id } : u));
    setEditingDeptId(null);
    setDropdownPos(null);
    try {
      await api.put(`/users/${userId}`, { department_id: dept?.id || null });
    } catch (error) {
      toast.error('Failed to update department');
      fetchUsers(false);
    }
  };

  const toggleMgrDept = async (userId, deptId, checked) => {
    const newDepts = checked
      ? [...editMgrDepts, deptId]
      : editMgrDepts.filter(id => id !== deptId);
    setEditMgrDepts(newDepts);
    try {
      await api.put(`/users/manager-departments/${userId}`, { department_ids: newDepts });
      // Update the display map
      const deptObjects = newDepts.map(id => {
        const d = departments.find(dep => dep.id === id);
        return d ? { id: d.id, name: d.name } : { id, name: '?' };
      });
      setUserMgrDepts(prev => ({ ...prev, [userId]: deptObjects }));
    } catch (error) {
      toast.error('Failed to update managed departments');
      setEditMgrDepts(editMgrDepts);
    }
  };

  const addUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setAddingUser(true);
    try {
      await api.post('/users', newUser);
      toast.success('User created successfully');
      setShowAddModal(false);
      setNewUser({ name: '', email: '', role: 'employee', department_id: null });
      fetchUsers(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const openEditModal = async (e, user) => {
    e.stopPropagation();
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email, phone: user.phone || '', company_id: user.company_id || null });
    // Load managed departments if user is a manager
    if (user.role === 'manager' || user.role === 'admin') {
      try {
        const res = await api.get(`/users/manager-departments/${user.id}`);
        setEditMgrDepts((res.data.data || []).map(d => d.id));
      } catch { setEditMgrDepts([]); }
    } else {
      setEditMgrDepts([]);
    }
  };

  const saveEditUser = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      // Save managed departments if manager/admin
      if (editUser.role === 'manager' || editUser.role === 'admin') {
        await api.put(`/users/manager-departments/${editUser.id}`, { department_ids: editMgrDepts });
      }
      toast.success('User updated');
      setEditUser(null);
      fetchUsers(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deactivated');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'inactive' } : u));
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    const date = new Date(timeStr);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const getStatusClass = (status) => {
    const map = {
      present: 'status-present',
      late: 'status-late',
      absent: 'status-absent',
      approved_leave: 'status-approved-leave',
      training: 'status-training',
      holiday: 'status-holiday',
      hag: 'status-holiday',
      erev_hag: 'status-half',
      vacation: 'status-vacation',
      half_day_off: 'status-half',
      half_day: 'status-half',
      sick_leave: 'status-sick',
      sick_day: 'status-sick',
      child_sick: 'status-child-sick',
      miluim: 'status-miluim',
      childcare: 'status-childcare',
      business_trip: 'status-business-trip',
      wfh: 'status-wfh',
      sabbatical: 'status-sabbatical',
    };
    return map[status] || 'status-absent';
  };

  if (!isAdmin && !isManager) {
    return <div className="loading">Access denied</div>;
  }

  if (loading) return <div className="loading">Loading...</div>;

  const managerDeptIds = managerDepts.map(d => d.id);
  const deptUsers = isManager
    ? users.filter(u => managerDeptIds.includes(u.department_id) || u.id === currentUser.id)
    : users;
  let filteredUsers = showActiveOnly ? deptUsers.filter(u => u.status === 'active') : deptUsers;

  // Filter by department text search
  if (departmentFilter) {
    const search = departmentFilter.toLowerCase();
    filteredUsers = filteredUsers.filter(u =>
      (u.department?.name || '').toLowerCase().includes(search)
    );
  }

  // Filter by today's status if set
  if (todayStatusFilter === 'clocked_in') {
    filteredUsers = filteredUsers.filter(u => u.today_clocked_in);
  } else if (todayStatusFilter === 'sick') {
    filteredUsers = filteredUsers.filter(u => ['sick_leave', 'sick_day', 'child_sick'].includes(u.today_status));
  } else if (todayStatusFilter === 'missing') {
    const accountedStatuses = ['present', 'late', 'sick_leave', 'sick_day', 'child_sick', 'vacation'];
    filteredUsers = filteredUsers.filter(u => !accountedStatuses.includes(u.today_status));
  } else if (todayStatusFilter) {
    filteredUsers = filteredUsers.filter(u => u.today_status === todayStatusFilter);
  }
  const activeRoleUser = editingId ? users.find(u => u.id === editingId) : null;
  const activeStatusUser = editingStatusId ? users.find(u => u.id === editingStatusId) : null;
  const activeDeptUser = editingDeptId ? users.find(u => u.id === editingDeptId) : null;
  const activeCompanyUser = editingCompanyId ? users.find(u => u.id === editingCompanyId) : null;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>{isManager ? `My Team` : 'Users Management'}</h1>
        <div className="header-controls">
          {todayStatusFilter && (
            <span className={`today-filter-badge filter-${todayStatusFilter}`} onClick={() => setTodayStatusFilter(null)}>
              {todayStatusFilter === 'present' ? 'Present Today' :
               todayStatusFilter === 'late' ? 'Late Today' :
               todayStatusFilter === 'absent' ? 'Absent Today' :
               todayStatusFilter === 'clocked_in' ? 'Currently Working' :
               todayStatusFilter === 'sick' ? 'Sick Today' :
               todayStatusFilter === 'missing' ? 'Missing Status' :
               todayStatusFilter === 'vacation' ? 'Vacation Today' : todayStatusFilter}
              <span className="clear-filter">&times;</span>
            </span>
          )}
          <div className="dept-filter-wrapper" ref={deptFilterRef}>
            <div
              className="dept-filter-input"
              onClick={() => setDeptFilterOpen(!deptFilterOpen)}
            >
              {departmentFilter || 'All Departments'}
              {departmentFilter ? (
                <span className="dept-filter-clear" onClick={(e) => { e.stopPropagation(); setDepartmentFilter(''); setDeptFilterSearch(''); }}>&times;</span>
              ) : (
                <span className="dept-filter-arrow">&#9662;</span>
              )}
            </div>
            {deptFilterOpen && (
              <div className="dept-filter-dropdown">
                <input
                  type="text"
                  className="dept-filter-search"
                  placeholder="Search..."
                  value={deptFilterSearch}
                  onChange={(e) => setDeptFilterSearch(e.target.value)}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="dept-filter-list">
                  <div
                    className={`dept-filter-option ${!departmentFilter ? 'active' : ''}`}
                    onClick={() => { setDepartmentFilter(''); setDeptFilterOpen(false); setDeptFilterSearch(''); }}
                  >
                    All Departments
                  </div>
                  {departments
                    .filter(d => d.status === 'active' && d.name.toLowerCase().includes(deptFilterSearch.toLowerCase()))
                    .map(dept => (
                      <div
                        key={dept.id}
                        className={`dept-filter-option ${departmentFilter === dept.name ? 'active' : ''}`}
                        onClick={() => { setDepartmentFilter(dept.name); setDeptFilterOpen(false); setDeptFilterSearch(''); }}
                      >
                        {dept.name}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <label className="active-filter">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Active only
          </label>
          <span className="user-count">{filteredUsers.length} of {deptUsers.length} users</span>
          {isAdmin && (
            <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add User
            </button>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="modal-overlay" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{userDetail.user.name}</h2>
              <button className="modal-close" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>&times;</button>
            </div>
            <div className="modal-info">
              <span>{userDetail.user.email}</span>
              <span className={`role-badge role-${userDetail.user.role}`}>{userDetail.user.role}</span>
            </div>

            <div className="modal-month">
              <input
                type="month"
                value={userMonth}
                onChange={(e) => setUserMonth(e.target.value)}
                className="month-picker"
              />
            </div>

            <div className="summary-cards">
              <div className="summary-card green">
                <span className="card-number">{userDetail.summary.present_days}</span>
                <span className="card-label">Present</span>
              </div>
              <div className="summary-card yellow">
                <span className="card-number">{userDetail.summary.late_days}</span>
                <span className="card-label">Late</span>
              </div>
              <div className="summary-card red">
                <span className="card-number">{userDetail.summary.absent_days}</span>
                <span className="card-label">Absent</span>
              </div>
              <div className="summary-card blue">
                <span className="card-number">{userDetail.summary.leave_days}</span>
                <span className="card-label">Leave</span>
              </div>
              <div className="summary-card purple">
                <span className="card-number">{userDetail.summary.total_hours}h</span>
                <span className="card-label">Total Hours</span>
              </div>
              <div className="summary-card gray">
                <span className="card-number">{userDetail.summary.average_hours}h</span>
                <span className="card-label">Avg/Day</span>
              </div>
            </div>

            <div className="detail-table-wrapper">
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {userDetail.attendances.map((att) => (
                    <tr key={att.id}>
                      <td>{new Date(att.date + 'T12:00:00').toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                      <td>{formatTime(att.clock_in)}</td>
                      <td>{formatTime(att.clock_out)}</td>
                      <td>{att.total_hours ? `${parseFloat(att.total_hours).toFixed(1)}h` : '--'}</td>
                      <td><span className={`status-badge ${getStatusClass(att.status)}`}>{att.status.replace('_', ' ')}</span></td>
                    </tr>
                  ))}
                  {userDetail.attendances.length === 0 && (
                    <tr><td colSpan="5" style={{ textAlign: 'center', color: '#999' }}>No records</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {isManager && managerDepts.length > 1 ? (
        // Grouped by department for multi-department managers
        managerDepts.map(dept => {
          const deptGroupUsers = filteredUsers.filter(u => u.department_id === dept.id);
          if (deptGroupUsers.length === 0) return null;
          return (
            <div key={dept.id} className="dept-group">
              <h2 className="dept-group-title">{dept.name} <span className="dept-group-count">({deptGroupUsers.length})</span></h2>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptGroupUsers.map((user) => (
                      <tr key={user.id} className="user-row" onClick={() => setSelectedUser(user.id)}>
                        <td className="user-name-cell" onClick={(e) => { e.stopPropagation(); navigate(`/attendance?user_id=${user.id}`); }} style={{ cursor: 'pointer' }}>
                          <strong>{user.name}</strong>
                        </td>
                        <td onClick={(e) => { e.stopPropagation(); navigate(`/attendance?user_id=${user.id}`); }} style={{ cursor: 'pointer' }}>{user.email}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <span className={`role-badge role-${user.role}`}>{user.role}</span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <span className={`status-dot ${user.status === 'active' ? 'active' : 'inactive'}`}>
                            {user.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      ) : (
        // Flat table for admins or single-department managers
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {isAdmin && <th>Company</th>}
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                {isAdmin && <th style={{ width: '90px' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="user-row" onClick={() => setSelectedUser(user.id)}>
                  <td className="user-name-cell" onClick={(e) => { e.stopPropagation(); navigate(`/attendance?user_id=${user.id}`); }} style={{ cursor: 'pointer' }}>
                    <strong>{user.name}</strong>
                  </td>
                  <td onClick={(e) => { e.stopPropagation(); navigate(`/attendance?user_id=${user.id}`); }} style={{ cursor: 'pointer' }}>{user.email}</td>
                  {isAdmin && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <span
                        className="dept-badge"
                        onClick={(e) => openCompanyDropdown(e, user.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {user.company?.name || 'No Company'}
                      </span>
                    </td>
                  )}
                  <td onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                      <span
                        className="dept-badge"
                        onClick={(e) => openDeptDropdown(e, user.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {(user.role === 'manager' || user.role === 'admin') && userMgrDepts[user.id]?.length > 0
                          ? userMgrDepts[user.id].map(d => d.name).join(', ')
                          : user.department?.name || 'No Department'}
                      </span>
                    ) : (
                      <span className="dept-badge">{user.department?.name || 'No Department'}</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                      <span
                        className={`role-badge role-${user.role}`}
                        onClick={(e) => openRoleDropdown(e, user.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {user.role}
                      </span>
                    ) : (
                      <span className={`role-badge role-${user.role}`}>{user.role}</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <span
                      className={`status-dot ${user.status === 'active' ? 'active' : 'inactive'}`}
                      onClick={(e) => openStatusDropdown(e, user.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {user.status}
                    </span>
                  </td>
                  {isAdmin && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="edit-user-btn"
                        onClick={(e) => openEditModal(e, user)}
                        title="Edit user"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="delete-user-btn"
                        onClick={() => setDeleteConfirm(user)}
                        title="Delete user"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dropdowns rendered via portal on document.body */}
      {editingId && activeRoleUser && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="role-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left, maxHeight: dropdownPos.maxHeight, overflowY: 'auto' }}
        >
          {['employee', 'manager', 'admin'].map(role => (
            <div
              key={role}
              className={`role-option ${activeRoleUser.role === role ? 'active' : ''}`}
              onClick={() => updateUserRole(activeRoleUser.id, role)}
            >
              <span className={`role-badge role-${role}`}>{role}</span>
            </div>
          ))}
        </div>,
        document.body
      )}

      {editingStatusId && activeStatusUser && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="role-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left, maxHeight: dropdownPos.maxHeight, overflowY: 'auto' }}
        >
          {['active', 'inactive'].map(status => (
            <div
              key={status}
              className={`role-option ${activeStatusUser.status === status ? 'active' : ''}`}
              onClick={() => updateUserStatus(activeStatusUser.id, status)}
            >
              <span className={`status-dot ${status}`}>{status}</span>
            </div>
          ))}
        </div>,
        document.body
      )}

      {editingDeptId && activeDeptUser && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="dept-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left, maxHeight: dropdownPos.maxHeight }}
        >
          {(activeDeptUser.role === 'manager' || activeDeptUser.role === 'admin') && (
            <div className="dept-section-label">Managed Departments</div>
          )}
          <div className="dept-search-row">
            <input
              type="text"
              className="dept-search-input"
              placeholder="Search department..."
              value={deptSearch}
              onChange={(e) => setDeptSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="dept-options-list">
            {departmentsLoading ? (
              <div className="dept-loading">
                <svg className="spinner" viewBox="0 0 24 24" width="20" height="20">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" />
                </svg>
                Loading...
              </div>
            ) : (activeDeptUser.role === 'manager' || activeDeptUser.role === 'admin') ? (
              /* Multi-select checkboxes for managers */
              departments
                .filter(dept => (!dept.status || dept.status === 'active') && dept.name.toLowerCase().includes(deptSearch.toLowerCase()))
                .map(dept => (
                  <label
                    key={dept.id}
                    className={`dept-option dept-check-option ${editMgrDepts.includes(dept.id) ? 'active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={editMgrDepts.includes(dept.id)}
                      onChange={(e) => toggleMgrDept(activeDeptUser.id, dept.id, e.target.checked)}
                    />
                    <span className="dept-badge">{dept.name}</span>
                  </label>
                ))
            ) : (
              <>
                {!deptSearch && (
                  <div
                    className={`dept-option ${!activeDeptUser.department_id ? 'active' : ''}`}
                    onClick={() => { updateUserDept(activeDeptUser.id, null); setDeptSearch(''); }}
                  >
                    <span className="dept-badge">No Department</span>
                  </div>
                )}
                {departments
                  .filter(dept => (!dept.status || dept.status === 'active') && dept.name.toLowerCase().includes(deptSearch.toLowerCase()))
                  .map(dept => (
                    <div
                      key={dept.id}
                      className={`dept-option ${activeDeptUser.department_id === dept.id ? 'active' : ''}`}
                      onClick={() => { updateUserDept(activeDeptUser.id, dept); setDeptSearch(''); }}
                    >
                      <span className="dept-badge">{dept.name}</span>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {editingCompanyId && activeCompanyUser && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="role-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left, maxHeight: dropdownPos.maxHeight, overflowY: 'auto' }}
        >
          <div
            className={`role-option ${!activeCompanyUser.company_id ? 'active' : ''}`}
            onClick={() => updateUserCompany(activeCompanyUser.id, null)}
          >
            <span className="dept-badge">No Company</span>
          </div>
          {companies.filter(c => c.status === 'active').map(c => (
            <div
              key={c.id}
              className={`role-option ${activeCompanyUser.company_id === c.id ? 'active' : ''}`}
              onClick={() => updateUserCompany(activeCompanyUser.id, c.id)}
            >
              <span className="dept-badge">{c.name}</span>
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content add-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <div className="add-user-form">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="form-field">
                <label>Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {companies.length > 0 && (
                <div className="form-field">
                  <label>Company</label>
                  <select
                    value={newUser.company_id || ''}
                    onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">No Company</option>
                    {companies.filter(c => c.status === 'active').map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-field">
                <label>Department</label>
                <select
                  value={newUser.department_id || ''}
                  onChange={(e) => setNewUser({ ...newUser, department_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">No Department</option>
                  {departments.filter(d => d.status === 'active').map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <p className="form-note">Default password: password123</p>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn-save" onClick={addUser} disabled={addingUser}>
                  {addingUser ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal-content add-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={() => setEditUser(null)}>&times;</button>
            </div>
            <div className="add-user-form">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="form-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              {companies.length > 0 && (
                <div className="form-field">
                  <label>Company</label>
                  <select
                    value={editForm.company_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, company_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">No Company</option>
                    {companies.filter(c => c.status === 'active').map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {(editUser.role === 'manager' || editUser.role === 'admin') && (
                <div className="form-field">
                  <label>Managed Departments</label>
                  <div className="mgr-dept-checkboxes">
                    {departments.filter(d => !d.status || d.status === 'active').map(dept => (
                      <label key={dept.id} className="mgr-dept-check">
                        <input
                          type="checkbox"
                          checked={editMgrDepts.includes(dept.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditMgrDepts(prev => [...prev, dept.id]);
                            } else {
                              setEditMgrDepts(prev => prev.filter(id => id !== dept.id));
                            }
                          }}
                        />
                        {dept.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setEditUser(null)}>Cancel</button>
                <button className="btn-save" onClick={saveEditUser} disabled={savingEdit}>
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deactivate User</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="delete-confirm-content">
              <p>Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>?</p>
              <p className="delete-warning">The user will no longer be able to log in. You can reactivate them later by changing their status.</p>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="btn-delete" onClick={() => deleteUser(deleteConfirm.id)}>Deactivate User</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
